import {Fragment, type PropsWithChildren, useState} from 'react'
import {Link} from '@primer/react'
import {clsx} from 'clsx'
import {ChevronRightIcon, GitBranchIcon, ChevronDownIcon} from '@primer/octicons-react'
import type {LegacyCodeResults, LegacyCodeResultItem} from '../../types/blackbird-types'
import Result from '../search-result'
import LanguageCircle from '../LanguageCircle'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {blobPath, encodePart, ownerAvatarPath} from '@github-ui/paths'
import EllipsisOverflow from '../EllipsisOverflow'
import {GitHubAvatar} from '@github-ui/github-avatar'
import styles from './LegacyCode.module.css'

export default function LegacyCode({results}: {results: LegacyCodeResults}) {
  return (
    <Result.List className={styles.responsiveGapList}>
      {results.results.map(item => (
        <LegacyCodeResult key={item.id} item={item} />
      ))}
    </Result.List>
  )
}

function LegacyCodeResult({item}: {item: LegacyCodeResultItem}) {
  const [isOpen, setIsOpen] = useState(true)

  const destinationPath = blobPath({
    owner: item.owner_login,
    repo: item.repo.repository.name,
    commitish: item.commit_sha,
    filePath: item.path,
  })

  return (
    <div className={styles.resultWrapper}>
      <div className={clsx(styles.headerBar, !isOpen && styles.headerBarClosed)}>
        <OpenCloseButton
          isOpen={isOpen}
          onClick={() => setIsOpen(!isOpen)}
          name={`${item.owner_login}/${item.repo.repository.name}`}
        />
        <div className={styles.headerContent}>
          <Result.Avatar marginRight="6px">
            <GitHubAvatar square className={styles.avatar} size={16} src={ownerAvatarPath({owner: item.owner_login})} />
          </Result.Avatar>
          <Result.Title small>
            <EllipsisOverflow side="left" title={`${item.owner_login}/${item.repo.repository.name} · item.hl_path`}>
              <Result.RepositoryLink
                owner={item.owner_login}
                name={item.repo.repository.name}
                style={{color: 'var(--fgColor-default)', fontWeight: 'bold'}}
              />
              &nbsp;·&nbsp;
              <Link href={destinationPath} className={styles.filePathLink}>
                {item.hl_path ? <Result.SearchMatchText text={item.hl_path} /> : <span>{item.path}</span>}
              </Link>
            </EllipsisOverflow>
          </Result.Title>
        </div>
        <div className={styles.footerWrapper}>
          <Result.Footer>
            {item.language && (
              <Result.FooterItem>
                <IconWithText ariaLabel={`${item.language} language`} text={item.language}>
                  <div className={styles.languageCircle}>
                    <LanguageCircle color={item.language_color ?? 'gray'} />
                  </div>
                </IconWithText>
              </Result.FooterItem>
            )}
            {item.repo_default_branch && (
              <Result.FooterItem>
                <IconWithText
                  ariaLabel={`${item.repo_default_branch} branch`}
                  text={item.repo_default_branch}
                  textUrl={`${item.repo.repository.name}/tree/${encodePart(item.repo_default_branch)}`}
                >
                  <GitBranchIcon size={16} className={styles.branchIcon} />
                </IconWithText>
              </Result.FooterItem>
            )}
          </Result.Footer>
        </div>
      </div>
      <Result.Header />

      {isOpen && (
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        <div className={clsx('notranslate', styles.codeWrapper)}>
          <CodeSnippetFrame>
            <LegacyCodeSnippet item={item} />
          </CodeSnippetFrame>
        </div>
      )}
    </div>
  )
}

function OpenCloseButton({isOpen, onClick, name}: {isOpen: boolean; onClick: () => void; name: string}) {
  return (
    <button onClick={onClick} aria-label={name} aria-expanded={isOpen} className={styles.openCloseButton}>
      {isOpen ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
    </button>
  )
}

function LegacyCodeSnippet({item}: {item: LegacyCodeResultItem}) {
  return (
    <table>
      <tbody>
        {item.hl_fragments.map((fragment, i) => (
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <Fragment key={i}>
            {fragment.lines.map(({line_number, line}) => (
              <tr key={line_number}>
                <td className={clsx('blob-num', styles.blobNumCell)}>
                  <Link
                    href={blobPath({
                      owner: item.owner_login,
                      repo: item.repo.repository.name,
                      commitish: item.commit_sha,
                      filePath: item.path,
                    })}
                    className={styles.lineNumberLink}
                  >
                    {line_number + 1}
                  </Link>
                </td>
                {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
                <VerifiedHTMLText as="td" className="blob-code blob-code-inner" html={line} />
              </tr>
            ))}
            {i === item.hl_fragments.length - 1 ? null : <FragmentDivider />}
          </Fragment>
        ))}
      </tbody>
    </table>
  )
}

function FragmentDivider() {
  return (
    <tr className={styles.separatorRow}>
      <td className={clsx('blob-num', styles.blobNumCell)}>&hellip;</td>
      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <td className="blob-code blob-code-inner" />
    </tr>
  )
}

function CodeSnippetFrame({children}: PropsWithChildren) {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
  return <div className={clsx('code-list', 'notranslate', styles.codeSnippetFrame)}>{children}</div>
}

interface IconWithTextProps extends PropsWithChildren {
  ariaLabel: string
  text: string
  textUrl?: string
}

function IconWithText({ariaLabel, text, textUrl, children}: IconWithTextProps) {
  const inner = (
    <div className={styles.iconWithText}>
      {children}
      {/* eslint-disable-next-line github/a11y-role-supports-aria-props */}
      <span aria-label={ariaLabel}>{text}</span>
    </div>
  )

  if (!textUrl) return inner

  return (
    <Link href={textUrl} className={styles.mutedLink}>
      {inner}
    </Link>
  )
}
